import React from 'react';
import type { FormInstance } from 'antd';
import { Button, Col, Form, Input, Row, Select } from 'antd';
import { Plus, Trash2, ChevronDown } from 'lucide-react';
import styles from '../../pages/UpdatePersonalProfilePage.module.css';

const { Option } = Select;

const RELATIONSHIP_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'spouse', label: 'Vợ/Chồng' },
  { value: 'parent', label: 'Cha/Mẹ' },
  { value: 'child', label: 'Con' },
  { value: 'sibling', label: 'Anh/Chị/Em' },
  { value: 'grandparent', label: 'Ông/Bà' },
  { value: 'grandchild', label: 'Cháu' },
  { value: 'other', label: 'Khác' },
];

interface UpdateFamilyDependentsSectionProps {
  /**
   * userId của nhân sự cần hiển thị thông tin gia đình/người phụ thuộc.
   * - Nếu truyền vào: ưu tiên dùng userId này (màn Quản lý nhân sự chỉnh hồ sơ staff).
   * - Nếu không truyền: fallback về user hiện tại từ AuthContext (trang hồ sơ cá nhân tự cập nhật).
   */
  userId?: number;
  form: FormInstance;
}

export const UpdateFamilyDependentsSection: React.FC<UpdateFamilyDependentsSectionProps> = () => {
  return (
    <div className={styles.section}>
      <Form.List name="familyDependents">
        {(fields, { add, remove }) => (
          <div className={styles.familySection}>
            {fields.length === 0 && (
              <div className={styles.familyEmptyState}>
                <div style={{ marginBottom: 16 }}>
                  Chưa có thành viên nào. Nhấn &quot;Thêm thành viên&quot; để bắt đầu.
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="primary"
                    icon={<Plus size={16} />}
                    size="middle"
                    className={styles.familyAddButton}
                    onClick={() => add({})}
                  >
                    Thêm thành viên
                  </Button>
                </div>
              </div>
            )}

            {fields.map((field, index) => (
              <div key={field.key} className={styles.familyCard}>
                <div className={styles.familyCardHeader}>
                  <span className={styles.familyCardTitle}>Thành viên {index + 1}</span>
                  <Button
                    type="text"
                    danger
                    icon={<Trash2 size={16} />}
                    className={styles.familyRemoveButton}
                    onClick={() => remove(field.name)}
                  >
                    Xóa
                  </Button>
                </div>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={8}>
                    <Form.Item name={[field.name, 'relationship']} label="Mối quan hệ">
                      <Select
                        placeholder="Chọn mối quan hệ"
                        size="large"
                        className={styles.select}
                        suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
                        allowClear
                      >
                        {RELATIONSHIP_OPTIONS.map((opt) => (
                          <Option key={opt.value} value={opt.value}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={[field.name, 'fullName']} label="Họ và tên">
                      <Input placeholder="Họ và tên" size="large" className={styles.input} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={[field.name, 'birthYear']} label="Năm sinh">
                      <Input placeholder="Năm sinh" size="large" className={styles.input} inputMode="numeric" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'occupation']} label="Nghề nghiệp">
                      <Input placeholder="Nghề nghiệp" size="large" className={styles.input} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'workplace']} label="Nơi làm việc">
                      <Input placeholder="Nơi làm việc" size="large" className={styles.input} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'phone']} label="Số điện thoại">
                      <Input placeholder="Số điện thoại" size="large" className={styles.input} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'address']} label="Địa chỉ">
                      <Input placeholder="Địa chỉ" size="large" className={styles.input} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            ))}

            {/* Nút Thêm thành viên ở cuối danh sách */}
            {fields.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Button
                  type="primary"
                  icon={<Plus size={16} />}
                  size="middle"
                  className={styles.familyAddButton}
                  onClick={() => add({})}
                >
                  Thêm thành viên
                </Button>
              </div>
            )}
          </div>
        )}
      </Form.List>
    </div>
  );
};
